import React, { useContext, useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { Container } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { InstantSearch } from 'react-instantsearch-dom';
import { useHistory, useParams } from 'react-router-dom';

import { Loader } from '../../../../components/Loader';
import RouteWithCode from '../../../../components/RouteWithCode/RouteWithCode';
import { SeoBlock } from '../../../../components/SeoBlock';
import { RM_TAGS } from '../../../../const';
import { DiscoverContext } from '../../../../context/DiscoverContext';
import { useRmTag } from '../../../../context/RmContext';
import useScriptLoader, { SCRIPTS } from '../../../../context/ScriptLoader/useScriptLoader';
import { GET_CATEGORIES_BY_PARENT, GET_VIDEO_CATEGORY } from '../../../../graphql/videos';
import {
  GetCategoriesByParentArgs,
  GetVideoCategoryArgs,
  GetVideoCategoryData,
  LearnerInfo,
  ListVideoCategoriesData,
  Tenant,
} from '../../../../types';
import { generateSeoTitle, translate } from '../../../../utils/helpers';
import { useClearSearchState } from '../../../../utils/hooks';
import NotFoundPage from '../../../NotFoundPage';
import Breadcrumbs from '../../components/Breadcrumbs';
import { CategoryInfo } from '../../components/CategoryInfo/CategoryInfo';
import { Header } from '../../components/Header';
import { useCommonHeaderStyles } from '../../components/Header/style';
import SERPContainer from '../../components/SERPContainer';
import { Search } from '../../components/Search';
import { SubcategoriesBlock } from '../../components/SubcategoriesBlock';
import { Tabs } from '../../components/Tabs';
import { DEFAULT_URL } from '../../const';
import useStyles from './styles';

const getNavigationArray = (categoryInfo) => {
  let navigationArr = [
    {
      title: translate('page.discover.discover'),
      url: DEFAULT_URL,
    },
  ];

  if (categoryInfo?.parent) {
    navigationArr.push({
      title: categoryInfo?.parent?.title,
      url: `/${categoryInfo?.parent?.slug}`,
    });
  }

  if (categoryInfo) {
    navigationArr.push({
      title: categoryInfo.title,
      url: `/${categoryInfo.slug}`,
    });
  }

  return navigationArr;
};

interface CategoryPageProps {
  tenant: Tenant;
  algoliaIndexName: string;
  searchClient: any;
  userInfo?: LearnerInfo;
  loadingKey: boolean;
  logoUrl?: string;
}

export const CategoryPage: React.FC<CategoryPageProps> = ({
  tenant,
  algoliaIndexName,
  searchClient,
  userInfo,
  loadingKey,
  logoUrl,
}) => {
  useScriptLoader(SCRIPTS.TEAMS_SHARE);
  const { t } = useTranslation();
  const headerClasses = useCommonHeaderStyles();
  const { searchState, setSearchState, setCategoryTitle } = useContext(DiscoverContext);
  const params = useParams();
  const { category, subcategory } = params;
  const history = useHistory();

  //clear search state on page change
  useClearSearchState(true);
  useRmTag(RM_TAGS.CategoryPage);

  const currCategory = subcategory ? subcategory : category;

  const { data: categoryData, loading: loadingCategory } = useQuery<
    GetVideoCategoryData,
    GetVideoCategoryArgs
  >(GET_VIDEO_CATEGORY, {
    variables: {
      filter: {
        slug: currCategory,
      },
    },
  });

  const categoryInfo = categoryData?.getVideoCategory;

  const { data: childData, loading: loadingChildData } = useQuery<
    ListVideoCategoriesData,
    GetCategoriesByParentArgs
  >(GET_CATEGORIES_BY_PARENT, {
    variables: {
      parent_id: categoryInfo?.id ? [categoryInfo.id] : null,
    },
    skip: !!subcategory || loadingCategory || !categoryInfo?.id,
  });

  const childCategories = childData?.listVideoCategories;
  const hasChildCategories = childCategories && childCategories.length > 0;
  const hasImage = !!categoryInfo?.image_url;

  const handleSearch = (val) => {
    setSearchState((prevState) => ({
      ...prevState,
      query: val,
    }));
    history.push(`${DEFAULT_URL}/search`);
  };

  const navigationArr = getNavigationArray(categoryInfo);

  useEffect(() => {
    if (categoryInfo) {
      const categoryObj = {
        'categories.lvl0': categoryInfo?.parent
          ? `${categoryInfo?.parent?.title} > ${categoryInfo?.title}`
          : categoryInfo?.title,
      };

      setSearchState((prevState) => ({
        ...prevState,
        hierarchicalMenu: {
          ...categoryObj,
        },
      }));

      if (categoryInfo?.parent) {
        setCategoryTitle(categoryInfo?.parent?.title);
      }
    }
  }, [categoryInfo, setSearchState, setCategoryTitle]);

  const classes = useStyles(hasChildCategories)();
  const canonicalUrl = categoryInfo?.parent?.slug
    ? `/${categoryInfo.parent.slug}/${categoryInfo?.slug}`
    : `/${categoryInfo?.slug}`;

  if (loadingCategory || loadingChildData || loadingKey) {
    return (
      <>
        {categoryInfo && (
          <SeoBlock
            data={{
              seo_title: generateSeoTitle(categoryInfo?.title, tenant?.title),
              seo_description: categoryInfo?.description || '',
              share_image: categoryInfo?.image_url,
            }}
            canonicalUrl={canonicalUrl}
          />
        )}
        <Loader />
      </>
    );
  }

  if (!categoryInfo) {
    return (
      <RouteWithCode code={404}>
        <NotFoundPage />
      </RouteWithCode>
    );
  }

  return (
    <>
      <SeoBlock
        data={{
          seo_title: generateSeoTitle(categoryInfo?.title, tenant?.title),
          seo_description: categoryInfo?.description || '',
          share_image: categoryInfo?.image_url,
        }}
        canonicalUrl={canonicalUrl}
      />
      <InstantSearch
        searchState={searchState}
        onSearchStateChange={setSearchState}
        searchClient={searchClient}
        indexName={algoliaIndexName}
      >
        <Header
          title={t('page.discover.discover')}
          imageUrl={categoryInfo?.image_url}
          wrapperClass={hasImage ? classes.withImageWrapper : ''}
          contentWrapperClass={hasImage ? classes.withImageContentWrapper : ''}
          tenantName={tenant?.name}
        >
          <Container className={headerClasses.centeredContainer}>
            <Search wrapperClass={headerClasses.search} refine={handleSearch} withClear />
            <Tabs />
          </Container>
          {hasImage && (
            <Container className={classes.withImgContainer}>
              <Breadcrumbs navigation={navigationArr} />
              <CategoryInfo title={categoryInfo?.title} description={categoryInfo?.description} />
              {hasChildCategories && (
                <SubcategoriesBlock
                  wrapperClass={classes.headerCategoriesWrapper}
                  childCategories={childCategories}
                  categorySlug={category}
                />
              )}
            </Container>
          )}
        </Header>
        {(!hasImage || hasChildCategories) && (
          <Container
            className={hasImage ? classes.childCategoriesContainer : classes.withoutImageContainer}
          >
            {!hasImage && (
              <>
                <Breadcrumbs navigation={navigationArr} />
                <CategoryInfo title={categoryInfo?.title} description={categoryInfo?.description} />
              </>
            )}
            {hasChildCategories && (
              <SubcategoriesBlock
                wrapperClass={classes.categoriesWrapper}
                childCategories={childCategories}
                categorySlug={category}
              />
            )}
          </Container>
        )}
        <Container>
          <SERPContainer
            logoUrl={logoUrl}
            userInfo={userInfo}
            tenantTitle={tenant?.title}
            tenantName={tenant?.name}
            linkedInId={tenant?.linkedin_id}
          />
        </Container>
      </InstantSearch>
    </>
  );
};

export default CategoryPage;
